/**
 * Calculates the actual text content bounds within a text box container.
 * Uses Range.getClientRects() for accurate text measurement, which gives us
 * the exact rendered text position without line-height padding.
 */

// Matches invisible characters: NBSP, zero-width space/joiner/non-joiner, and BOM
const INVISIBLE_CHARS_REGEX = /^[\u00A0\u200B-\u200D\uFEFF]+$/

export type TextContentBounds = {
  offsetLeft: number
  offsetTop: number
  offsetRight: number
  offsetBottom: number
  contentWidth: number
  contentHeight: number
}

/**
 * Gets the actual bounds of text content within a TinyMCE body element.
 * Uses Range.getClientRects() for accurate measurement of rendered text position,
 * avoiding line-height padding that causes false bleed warnings.
 *
 * @param bodyElement - TinyMCE's body element containing rendered text
 * @returns Offset values from container edges to actual text, or null if not measurable
 */
export const getTextContentBounds = (
  bodyElement: HTMLElement | null,
): TextContentBounds | null => {
  if (!bodyElement) return null

  // Get container bounds
  const containerRect = bodyElement.getBoundingClientRect()

  // Skip if container has no dimensions
  if (containerRect.width === 0 || containerRect.height === 0) return null

  // Use TreeWalker to find all text nodes for horizontal measurement
  const walker = document.createTreeWalker(
    bodyElement,
    NodeFilter.SHOW_TEXT,
    null,
  )

  let minLeft = Infinity
  let maxRight = -Infinity
  let minTop = Infinity
  let maxBottom = -Infinity
  let foundValidContent = false

  let textNode = walker.nextNode()
  while (textNode) {
    // Skip empty, whitespace-only, or invisible character text nodes
    const content = textNode.textContent?.trim()
    if (content && !INVISIBLE_CHARS_REGEX.test(content)) {
      // Create a range around this text node to measure it
      const range = document.createRange()
      range.selectNodeContents(textNode)

      // getClientRects() returns a rect for each line of text
      const rects = range.getClientRects()

      for (let i = 0; i < rects.length; i++) {
        const rect = rects[i]
        // Skip zero-dimension rects
        if (rect.width === 0 || rect.height === 0) continue

        foundValidContent = true
        minLeft = Math.min(minLeft, rect.left)
        maxRight = Math.max(maxRight, rect.right)
        minTop = Math.min(minTop, rect.top)
        maxBottom = Math.max(maxBottom, rect.bottom)
      }
    }
    textNode = walker.nextNode()
  }

  // If no valid text found, return null
  if (!foundValidContent) return null

  // Calculate offsets relative to container (in screen pixels)
  // Using text node bounds directly to avoid line-height padding issues
  const offsetLeft = minLeft - containerRect.left
  const offsetTop = minTop - containerRect.top
  const offsetRight = containerRect.right - maxRight
  const offsetBottom = containerRect.bottom - maxBottom

  return {
    offsetLeft: Math.max(0, offsetLeft),
    offsetTop: Math.max(0, offsetTop),
    offsetRight: Math.max(0, offsetRight),
    offsetBottom: Math.max(0, offsetBottom),
    contentWidth: Math.max(0, maxRight - minLeft),
    contentHeight: Math.max(0, maxBottom - minTop),
  }
}
